import {zapi} from '../__internal__/http-client/index.js';

import * as endpoints from './endpoints.js';

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-v3login
 * @param {$ZAPI.Account.LoginRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV3>}
 */
export const loginAccountV3 = (data) => zapi({
    path: 'v3/account/login',
    method: 'post',
    endpointID: endpoints.ACCOUNT_V3_LOGGED_IN,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-v3login
 * @param {$ZAPI.Account.OAuthLoginRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV3>}
 */
export const OAuthLoginAccountV3 = (data) => zapi({
    path: 'v3/account/login',
    method: 'post',
    endpointID: endpoints.ACCOUNT_V3_OAUTH_LOGGED_IN,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-v3ssologin
 * @param {$ZAPI.Account.SSOLoginRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV3>}
 */
export const SSOLoginAccountV3 = ({provider, ...data}) => zapi({
    path: `v3/account/login/${provider}`,
    method: 'post',
    endpointID: endpoints.ACCOUNT_V3_SSO_LOGGED_IN,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-v3logout
 * @returns {Promise<$ZAPI.Session.SessionResponseV3>}
 */
export const logoutAccountV3 = () => zapi({
    path: 'v3/account/logout',
    method: 'post',
    endpointID: endpoints.ACCOUNT_V3_LOGGED_OUT,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-createlogintoken
 * @param {$ZAPI.Account.CreateLoginTokenRequest} data
 * @returns {Promise<$ZAPI.Account.LoginTokenResponse>}
 */
export const createLoginToken = (data) => zapi({
    path: 'create_login_token',
    method: 'post',
    endpointID: endpoints.ACCOUNT_LOGIN_TOKEN_CREATED,
    data,
});

/**
 * @param {$ZAPI.Account.OAuthCodeRequest} data
 * @returns {Promise<$ZAPI.Account.OAuthCodeResponse>}
 */
export const getOAuthCode = (data) => zapi({
    path: 'oauth/authorization_code',
    method: 'post',
    endpointID: endpoints.OAUTH_CODE_REQUESTED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-addprivacysetting
 * @param {$ZAPI.Account.PrivacySetting} privacySetting
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const addPrivacySetting = (privacySetting) => zapi({
    path: `account/privacy_setting/${privacySetting}/add`,
    method: 'post',
    endpointID: endpoints.PRIVACY_SETTING_ADDED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-removeprivacysetting
 * @param {$ZAPI.Account.PrivacySetting} privacySetting
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const removePrivacySetting = (privacySetting) => zapi({
    path: `account/privacy_setting/${privacySetting}/remove`,
    method: 'post',
    endpointID: endpoints.PRIVACY_SETTING_REMOVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-userprofile
 * @returns {Promise<$ZAPI.Account.ProfileResponse>}
 */
export const getProfile = () => zapi({
    path: 'account/profile',
    method: 'get',
    endpointID: endpoints.ACCOUNT_PROFILE_RECEIVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-updateaccount
 * @param {$ZAPI.Account.UpdateRequest} data
 * @returns {Promise<$ZAPI.Account.UpdateResponse>}
 */
export const updateAccount = (data) => zapi({
    path: 'account/update',
    method: 'post',
    endpointID: endpoints.ACCOUNT_UPDATED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-deleteaccount
 * @param {$ZAPI.Account.DeleteRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const deleteAccount = (data) => zapi({
    path: 'account/delete',
    method: 'post',
    endpointID: endpoints.ACCOUNT_DELETED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-forgotpassword
 * @param {$ZAPI.Account.ResetPasswordRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const resetPasswordAnonymously = (data) => zapi({
    path: 'forgot-password',
    method: 'post',
    endpointID: endpoints.PASSWORD_RESET_REQUESTED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-forgotpassword
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const resetPassword = () => zapi({
    path: 'forgot-password',
    method: 'get',
    endpointID: endpoints.PASSWORD_RESET_REQUESTED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-resetpassword
 * @param {$ZAPI.Account.ChangePasswordRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const changePassword = (data) => zapi({
    path: 'reset_password',
    method: 'post',
    endpointID: endpoints.PASSWORD_CHANGED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-getapisig
 * @param {$ZAPI.Account.APISignatureRequest} data
 * @returns {Promise<$ZAPI.Account.APISignatureResponse>}
 */
export const getAPISignature = (data) => zapi({
    path: 'account/apisig',
    method: 'get',
    endpointID: endpoints.API_SIGNATURE_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-getpaymentmethods
 * @returns {Promise<$ZAPI.Account.PaymentMethodsResponse>}
 */
export const getPaymentMethods = () => zapi({
    path: 'account/payment_methods',
    method: 'get',
    endpointID: endpoints.PAYMENT_METHODS_RECEIVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-gettransactiondata
 * @param {$ZAPI.Account.TransactionsRequest} [data]
 * @returns {Promise<$ZAPI.Account.TransactionsResponse>}
 */
export const getTransactions = (data) => zapi({
    path: 'account/transactions',
    method: 'get',
    endpointID: endpoints.TRANSACTIONS_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-updatepaymentmethod
 * @param {$ZAPI.Account.UpdatePaymentMethodRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const updatePaymentMethod = (data) => zapi({
    path: 'account/payment_method/update',
    method: 'post',
    endpointID: endpoints.PAYMENT_METHOD_UPDATED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-updatepaymentmethod
 * @param {$ZAPI.Account.RemovePaymentMethodRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const removePaymentMethod = (data) => zapi({
    path: 'account/payment_method/remove',
    method: 'post',
    endpointID: endpoints.PAYMENT_METHOD_REMOVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-updatecreditcardexpiration
 * @param {$ZAPI.Account.UpdateCreditCardExpirationRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const updateCreditCardExpiration = (data) => zapi({
    path: 'account/payment_method/ccexp_update',
    method: 'post',
    endpointID: endpoints.CREDIT_CARD_EXPIRATION_UPDATED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-getbillingaddress
 * @returns {Promise<$ZAPI.Account.BillingAddressResponse>}
 */
export const getBillingAddress = () => zapi({
    path: 'account/billing_address',
    method: 'get',
    endpointID: endpoints.BILLING_ADDRESS_RECEIVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-updatebillingaddress
 * @param {$ZAPI.Account.BillingAddress} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const updateBillingAddress = (data) => zapi({
    path: 'account/billing_address/update',
    method: 'post',
    endpointID: endpoints.BILLING_ADDRESS_UPDATED,
    useJSONData: true,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-subscribe
 * @param {$ZAPI.Account.SubscribeRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const subscribe = (data) => zapi({
    path: 'account/subscribe',
    method: 'post',
    endpointID: endpoints.SUBSCRIBE_REQUESTED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-updateautorenew
 * @param {$ZAPI.Account.UpdateAutorenewRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const updateAutorenew = (data) => zapi({
    path: 'account/subscription/autorenew',
    method: 'post',
    endpointID: endpoints.AUTORENEW_UPDATED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4.1+-+B2C+Subscription+Management#2.4.1-B2CSubscriptionManagement-cancelautorenew
 * @param {$ZAPI.Account.CancelAutorenewRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const cancelAutorenew = (data) => zapi({
    path: 'account/subscription/cancel',
    method: 'post',
    endpointID: endpoints.AUTORENEW_CANCELED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-getpin
 * @param {$ZAPI.Account.GetPinRequest} data
 * @returns {Promise<$ZAPI.Account.GetPinResponse>}
 */
export const getPin = (data) => zapi({
    path: 'account/get_pin',
    method: 'post',
    endpointID: endpoints.PIN_RECEIVED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-setpin
 * @param {$ZAPI.Account.SetPinRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const setPin = (data) => zapi({
    path: 'account/pin',
    method: 'put',
    endpointID: endpoints.PIN_UPDATED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-getpintoggle
 * @returns {Promise<$ZAPI.Account.GetPinStateResponse>}
 */
export const getPinState = () => zapi({
    path: 'account/pin_toggle',
    method: 'get',
    endpointID: endpoints.PIN_STATE_RECEIVED,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-setpintoggle
 * @param {$ZAPI.Account.SetPinStateRequest} data
 * @returns {Promise<$ZAPI.Response.SuccessResponse>}
 */
export const setPinState = (data) => zapi({
    path: 'account/pin_toggle',
    method: 'post',
    endpointID: endpoints.PIN_STATE_UPDATED,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-unsubscribenewsletter
 * @param {$ZAPI.Account.NewsletterUnsubscribeRequest} data
 * @returns {Promise<void>}
 */
export const newsletterUnsubscribe = (data) => zapi({
    path: 'account/sendalerts/unsubscribe',
    method: 'post',
    endpointID: endpoints.NEWSLETTER_UNSUBSCRIBE,
    data,
});

/**
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-v2createaccount
 * @param {$ZAPI.Account.CreateRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV2>}
 */
export const createAccount = (data) => zapi({
    path: 'v2/account/new',
    method: 'post',
    endpointID: endpoints.ACCOUNT_CREATED,
    data,
});

/**
 * @deprecated use {@link loginAccountV3}
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-v2login
 * @param {$ZAPI.Account.LoginRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV2>}
 */
export const login = (data) => zapi({
    path: 'v2/account/login',
    method: 'post',
    endpointID: endpoints.LOGGED_IN,
    data,
});

/**
 * @deprecated use {@link OAuthLoginAccountV3}
 * @param {$ZAPI.Account.OAuthLoginRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV2>}
 */
export const OAuthLogin = (data) => zapi({
    path: 'v2/account/login',
    method: 'post',
    endpointID: endpoints.OAUTH_LOGGED_IN,
    data,
});

/**
 * @deprecated use {@link SSOLoginAccountV3}
 * @see https://developer.zattoo.com/display/ZAPI/2.4+-+API+Calls+for+B2C+only#2.4-APICallsforB2Conly-v2ssologin
 * @param {$ZAPI.Account.SSOLoginRequest} data
 * @returns {Promise<$ZAPI.Session.SessionResponseV2>}
 */
export const SSOLogin = ({provider, ...data}) => zapi({
    path: `v2/account/login/${provider}`,
    method: 'post',
    endpointID: endpoints.SSO_LOGGED_IN,
    data,
});

/**
 * @deprecated use {@link logoutAccountV3}
 * @see https://developer.zattoo.com/display/ZAPI/2.2.2+-+Account+Management#2.2.2-AccountManagement-v2logout
 * @returns {Promise<$ZAPI.Session.SessionResponseV2>}
 */
export const logout = () => zapi({
    path: 'v2/account/logout',
    method: 'post',
    endpointID: endpoints.LOGGED_OUT,
});

/**
 * @param {string} [partnerId]
 * @returns {Promise<$ZAPI.Account.GetLoginTokenResponse>}
 * @todo Add link to docs
 */
export const getLoginToken = (partnerId) => zapi({
    path: 'login_token',
    method: 'get',
    endpointID: endpoints.LOGIN_TOKEN_OBTAINED,
    data: {
        ...(partnerId ? {partner_id: partnerId} : {}),
    },
});

/**
 * @param {string} code
 * @returns {Promise<$ZAPI.Account.VerifyLoginTokenResponse>}
 * @todo Add link to docs
 */
export const verifyLoginToken = (code) => zapi({
    path: `login_token/${code}`,
    method: 'get',
    endpointID: endpoints.LOGIN_TOKEN_VERIFIED,
});
